import type { GetServerSidePropsContext, GetServerSidePropsResult } from 'next'
import type { ParsedUrlQuery } from 'querystring'

import { wrapper } from '@hozana/redux/initStore'
import type { TWrappedGetServerSideData, TWrappedGetServerSideProps } from '@hozana/redux/types'
import { setRawCookie } from '@hozana/storage/cookies'
import { sanitize } from '@hozana/utils/functions/objects'

import type { TLocale } from 'i18n/constants'
import { PAGE } from 'routes/constants'

import { loadToken } from 'modules/auth/functions'
import { meProviderServerApiQueries } from 'modules/user/hooks/MeProvider'

import {
  TCommonPageProps,
  TGetCommonPagePropsOptions,
  TGetGetServerPropsProps,
  TServerApiQueries,
  TServerApiQueriesResult,
  TTitleGetter,
  createGetInitialData,
  dispatchTitle,
  getCommonPageProps,
  recordPageMetrics,
} from './common'

export const createGetServerSideData =
  <GAQ extends (ctx: GetServerSidePropsContext<ParsedUrlQuery>) => TServerApiQueries>(
    getApiQueries: GAQ,
  ): TWrappedGetServerSideData<TServerApiQueriesResult<ReturnType<GAQ>>, Parameters<GAQ>[0]['params']> =>
  async (ctx) => {
    const props = await createGetInitialData(getApiQueries(ctx) as ReturnType<GAQ>)({ ...ctx, asPath: ctx.resolvedUrl })

    if (Object.values(props).some((prop) => prop instanceof Error)) {
      return {
        notFound: true,
      }
    }

    return {
      props,
    }
  }

export const getMeServerSideData = createGetServerSideData(() => meProviderServerApiQueries)

type TGSSPResult<GDSSP extends TWrappedGetServerSideProps, CS extends boolean> = GetServerSidePropsResult<
  CS extends true
    ? TCommonPageProps & TGetGetServerPropsProps<GDSSP>
    : (TCommonPageProps & TGetGetServerPropsProps<GDSSP>) | TCommonPageProps
>

/**
 * ### createGetServerSideProps ###
 */
export const createGetServerSideProps =
  <GDSSP extends TWrappedGetServerSideProps, Context extends Parameters<GDSSP>[0], CS extends boolean = false>(
    pathname: PAGE,
    titleGetter: TTitleGetter<Context>,
    /** A getServerSideProps function for server side props dedicated to a page */
    getDedicatedServerSideProps?: GDSSP,
    commonPagePropsOptions: TGetCommonPagePropsOptions = {},
  ) =>
  async (context: Omit<Context, 'store'>): Promise<TGSSPResult<GDSSP, CS>> => {
    recordPageMetrics('getServerSideProps', pathname, context.locale as TLocale)

    // wrapper.getServerSideProps adds Redux on the server
    const serverSideProps = await wrapper.getServerSideProps((store) => async (ctx: Omit<Context, 'store'>) => {
      // Pass cookies to react-cookie so it may use them on SSR
      setRawCookie(ctx.req.headers.cookie)

      const contextWithStore = { ...ctx, store } as Context

      // Load data concurrently to go faster
      const [commonPageProps, dedicatedServerSideResult] = await Promise.all([
        getCommonPageProps({ ...ctx, pathname }, commonPagePropsOptions),
        getDedicatedServerSideProps?.(contextWithStore) ?? (Promise.resolve({ props: {} }) as ReturnType<GDSSP>),
        loadToken() ? getMeServerSideData(contextWithStore) : null,
      ])

      dispatchTitle(titleGetter, contextWithStore)

      return {
        ...dedicatedServerSideResult,
        ...('props' in dedicatedServerSideResult && {
          props: {
            ...commonPageProps,
            ...(await dedicatedServerSideResult.props),
          },
        }),
      } as TGSSPResult<GDSSP, CS>
    })(context)

    return sanitize(serverSideProps)
  }
